/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Button from '../../../components/form-control/Button';
import Input from '../../../components/form-control/Input';
import SelectDropdown from '../../../components/form-control/SelectDropdown';
import TextArea from '../../../components/form-control/TextArea';
import DatePickerInput from '../../../components/form-control/DatePickerInput';
import InputSelectMenu from '../../../components/form-control/InputSelectMenu';
import { CheckBoxInput } from '../../../components/form-control/CheckBoxInput';
import Loader from '../../../components/Loader';
import { adminCreateTask, getAllAdminAccounts, updateTask } from '../../../api/requests';
import useUserAccount from '../../../hooks/userAccountHook';
import { getFormattedAccessRight, showToast } from '../../../utils/miscFunctions';
import { CreateTaskProps, ICreateTaskFormProps } from '../../../types';
import { useQuery } from '@tanstack/react-query';
import { IRequestResponse } from '../../../resources/interfaces';

export const LEAD_TASK_TYPES = [
  'Intro call',
  'Attempt to convert 1',
  'Attempt to convert 2',
  'Share First Digital Content',
  'Share Second Digital Content',
  'Attempt to convert 3',
  'Attempt to convert 4',
  'Final Attempt to convert 5'
];

export const POLICY_TASK_TYPES = [
  'Document upload / request',
  'Payment resolution / request',
  'Referee setup/ verification',
  'Account info verification',
  'Vehicle info request',
  'Renewal setup'
];

export const CUSTOMER_TASK_TYPES = [
  // 'Highlights of policy terms and conditions',
  // 'Welcome Call / Message / Video',
  // 'Sign up to Fuel rebates',
  // 'Send welcome gift',
  // 'How to log in to account and check payments',
  // 'Invite to follow socials',
  // 'Invite to review',
  // 'Send video on renewal (Month 9)',
  // 'Users should now be asked to provide their WhatsApp number',
  // 'Users should provide their birthday'
  'Welcome call',
  'In-person review',
  'Highlights of policy terms',
  'How to login to account and check payments',
  'Provide whatsapp number',
  'Invite to review ',
  'Message to follow channels',
  'Message to follow social media pages',
  'Call on add-on services',
  'Payment completion message with Gift',
  'Call and message on due -later',
  'Call and message on renewal',
  'Thank you call for renewal'
];

const CreateTask = ({ onProceed, id, task, isEdit, type, selectedOption }: CreateTaskProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [admins, setAdmins] = useState<any[]>([]);
  const { user } = useUserAccount();

  const getAdmins = async () => {
    try {
      const response: any = await getAllAdminAccounts();
      if (!Array.isArray(response)) throw new Error('Invalid response from API');

      const _admins = response.map((admin: any) => ({
        name: `${admin.firstName} ${admin.lastName} (${getFormattedAccessRight(admin.accessRight)})`,
        value: admin.id
      }));

      setAdmins(_admins);
    } catch (error: any) {
      showToast.error(error?.description || 'Error fetching admins');
    }
  };

  const getTaskType = () => {
    switch (type) {
      case 'CUSTOMER':
        return CUSTOMER_TASK_TYPES;
      case 'LEAD':
        return LEAD_TASK_TYPES;
      case 'POLICY':
        return POLICY_TASK_TYPES;

      default:
        break;
    }
  };

  const submitHandler = async (values: any) => {

    const optionId = isOptionString ? String(selectedOption) : Object(selectedOption)?.id

    try {
      setSubmitting(true);
      const payload: ICreateTaskFormProps = {
        ...values,
        name: values.name,
        type: values.type,
        dueOn: values.dueOn,
        assignedTo: values.assignedTo,
        description: values.description,
        completed: values.completed
      };

      if (isEdit && id && (task?.id || optionId)) {
        const response = await updateTask(task?.id ?? optionId, payload);
        showToast.success(response?.message ?? 'Task updated successfully', );
      } else if (id) {
        const response = await adminCreateTask(type, id, payload);
        showToast.success(response?.message ?? 'Task created successfully', );
      }
      onProceed();
    } catch (error: IRequestResponse | any) {
      const errorMessage =
        error?.description || error?.message || 'An error occurred while processing your request.';
      showToast.error(errorMessage, );
    } finally {
      setSubmitting(false);
    }
  };


  const { isLoading } = useQuery({
    queryKey: ['getAdmins'],
    queryFn: getAdmins
  });

  const isOptionString = typeof selectedOption === 'string';

  return (
    <>

      {!isLoading && admins.length > 0 && (
        <div className="p-8">
          <Formik
            initialValues={{
              name: task ? task?.name : isOptionString ? String(selectedOption) : selectedOption?.name ?? '',
              type: task ? task?.type : isOptionString ? String(selectedOption) : selectedOption?.type ?? '',
              description: task ? task?.description : isOptionString ? String(selectedOption) : selectedOption?.description ?? '',
              dueOn: task && task?.dueOn ? new Date(task.dueOn) : isOptionString ? new Date(selectedOption as string) : selectedOption?.dueOn ? new Date(selectedOption.dueOn) : null,
              assignedTo: task ? task?.assignedTo?.id : isOptionString ? String(selectedOption) : selectedOption?.assignedTo?.id ?? user.id ?? '',
              completed: task ? task?.completed : isOptionString ? Boolean(selectedOption) : selectedOption?.completed ?? false
            }}
            validationSchema={Yup.object({
              name: Yup.string().trim().required('Task name is required.'),
              type: Yup.string().trim().required('Task type is required.'),
              description: Yup.string().trim().required('Task description is required.'),
              dueOn: Yup.date().required('Due date is required'),
              assignedTo: Yup.string().trim().required('Select admin to assign task')
            })}
            onSubmit={(values) => {
              submitHandler(values);
            }}>
            {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched }) => (
              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-5">
                  <Input
                    name="name"
                    label="Task name"
                    value={values.name}
                    type="text"
                    required
                    error={touched.name ? errors.name : undefined}
                    helperText={touched.name ? errors.name : undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-5">
                    {!isEdit && (
                      <InputSelectMenu
                        label="Task Type"
                        name="type"
                        listSelectedValue={values.type}
                        list={getTaskType()}
                        editable
                        onChange={(val: string | null) => setFieldValue('type', val)}
                        onBlur={handleBlur}
                        placeholder="Select or type a checklist"
                        error={touched.type ? errors.type : undefined}
                        helperText={touched.type ? errors.type : undefined}
                        required
                        wrapperClasses="w-full"
                      />
                    )}

                    <SelectDropdown
                      label="Assign to"
                      name="assignedTo"
                      listSelectedValue={values.assignedTo}
                      list={admins}
                      search
                      searchPlaceHolder="search by name"
                      onChange={(val: string | null) => setFieldValue('assignedTo', val)}
                      onBlur={handleBlur}
                      placeholder="Select an admin"
                      error={touched.assignedTo ? errors.assignedTo : undefined}
                      helperText={touched.assignedTo ? errors.assignedTo : undefined}
                      required
                      wrapperClasses="w-full"
                    />
                  </div>

                  <TextArea
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Description"
                    placeholder=""
                    required
                    error={touched.description ? errors.description : undefined}
                    helperText={touched.description ? errors.description : undefined}
                  />
                </div>

                <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-5  items-end">
                  <DatePickerInput
                    label="Due date"
                    id="dueOn"
                    selected={values.dueOn}
                    onChange={(val: Date | null) => {
                      setFieldValue('dueOn', val);
                    }}
                    allowEarlierSelections={false}
                    showMonthDropdown
                    showYearDropdown
                    showTimeSelect
                    placeholder="Due date"
                    error={touched.dueOn ? errors.dueOn : undefined}
                    helperText={touched.dueOn ? errors.dueOn : undefined}
                    required
                    dateFormat="dd/MM/yyyy hh:mm a"
                  />

                  <CheckBoxInput
                    label="Task Completed"
                    name="completed"
                    checked={values.completed}
                    onChange={(data: boolean) => setFieldValue('completed', data)}
                  />
                </div>
                
               <div className="w-full flex gap-4 mt-6 !mb-4">
                <Button
                  type="reset"
                  label="Reset"
                  bgColor='secondary'
                  fullWidth
                />
                <Button
                  type="submit"
                  label={isEdit ? 'Update' : 'Submit'}
                  bgColor='primary'
              fullWidth
                  loading={submitting}
                  disabled={submitting}
                />
              </div>
              </form>
            )}
          </Formik>
        </div>
      )}

      {isLoading && (
        <div className="h-[500px] flex items-center justify-center">
          <Loader message="Just a sec..." brollyLoader />
        </div>
      )}
    </>
  );
};

export default CreateTask;
